/**
 * The frame around sign-in, password change and MFA enrolment: a navy panel
 * that says what the product is, and the form on a card. On a phone the panel
 * shrinks to a header so the form is the first thing under the thumb.
 */
import type { ReactNode } from 'react';
import { BookCheck, FileCheck2, Landmark, ShieldCheck } from 'lucide-react';

const POINTS = [
  { Icon: FileCheck2, text: 'EOPT invoices with gapless BIR serials' },
  { Icon: Landmark, text: '2550Q, 1601-EQ, QAP, SAWT and SLSP from posted books' },
  { Icon: BookCheck, text: 'CAS-ready books, audit trail and period locks' },
  { Icon: ShieldCheck, text: 'Two-step sign-in and encrypted TINs' },
];

export function AuthLayout({ children }: { children: ReactNode }): JSX.Element {
  return (
    <div className="flex min-h-screen flex-col bg-slate-50 dark:bg-slate-950 lg:flex-row">
      <aside className="flex flex-col justify-between bg-gradient-to-br from-navy-900 via-navy to-navy-700 px-6 py-6 text-white lg:w-[44%] lg:px-12 lg:py-12">
        <div>
          <p className="text-2xl font-bold tracking-tight">
            CoreX<span className="text-brand-100">-lite</span>
          </p>
          <p className="mt-1 text-sm text-white/70">Cloud accounting for Philippine SMEs</p>
        </div>
        <div className="hidden lg:block">
          <h1 className="max-w-md text-3xl font-semibold leading-tight">BIR-ready by default.</h1>
          <p className="mt-3 max-w-md text-white/75">
            Every invoice, bill and payment carries the tax data your returns need — no quarter-end rebuild in Excel.
          </p>
          <ul className="mt-8 space-y-3">
            {POINTS.map(({ Icon, text }) => (
              <li key={text} className="flex items-center gap-3 text-sm text-white/85">
                <Icon aria-hidden="true" className="h-5 w-5 shrink-0 text-brand-100" />
                {text}
              </li>
            ))}
          </ul>
        </div>
        <p className="hidden text-xs text-white/60 lg:block">Powered by ITCORE Solutions Inc. · Cebu City</p>
      </aside>
      <main className="flex flex-1 items-center justify-center px-4 py-10">
        <div className="w-full max-w-md">{children}</div>
      </main>
    </div>
  );
}
